import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, Link } from "react-router-dom";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { BookOpen, ChevronLeft, AlertCircle, User } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import ClassList from "@/components/entities/ClassList";

function SchoolClassList({ schoolId }) {
  const { getToken } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/classes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load classes");
        setClasses(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [schoolId, getToken]);

  if (loading) return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />)}
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
      <AlertCircle size={15} />{error}
    </div>
  );

  if (classes.length === 0) return (
    <div className="flex flex-col items-center justify-center gap-2 py-14 text-center rounded-2xl border border-dashed border-border bg-muted/20">
      <BookOpen size={28} className="text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">No classes in this school yet</p>
    </div>
  );

  return (
    <ClassList
      items={classes}
      getLinkPath={(cls) => `/schools/classes/${cls._id}`}
      getOwnerName={(cls) => cls.ownerId?.name}
    />
  );
}

function ClassDetail({ schoolId, classId }) {
  const { getToken } = useAuth();
  const [school, setSchool] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, detailRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/classes/${classId}`, { headers }),
      ]);
      if (!detailRes.ok) {
        const body = await detailRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load class");
      }
      if (schoolRes.ok) setSchool(await schoolRes.json());
      setDetail(await detailRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, classId, getToken]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <PageContainer>
      <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
      <div className="h-48 bg-muted animate-pulse rounded-2xl" />
    </PageContainer>
  );

  if (error) return (
    <PageContainer>
      <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
        <AlertCircle size={15} />{error}
      </div>
    </PageContainer>
  );

  const label =
    detail?.name ||
    [detail?.grade && `Grade ${detail.grade}`, detail?.schoolYear].filter(Boolean).join(" · ");

  return (
    <PageContainer>
      <Link
        to="/schools/classes"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name} - Classes
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <BookOpen size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">{label}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {[detail?.grade && `Grade ${detail.grade}`, detail?.schoolYear, detail?.ownerId?.name]
              .filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <p className="text-sm font-medium text-foreground">
            Students ({detail?.reports?.length ?? 0})
          </p>
        </div>
        {detail?.reports?.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <User size={28} className="text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No student reports yet</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {detail?.reports?.map((report) => (
              <div key={report._id} className="flex items-center justify-between px-5 py-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <User size={14} className="text-muted-foreground shrink-0" />
                  <span className="text-foreground truncate">{report.studentName || "Unnamed student"}</span>
                </div>
                <span className="text-xs text-muted-foreground shrink-0 ml-4">{report.reportType}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        This is a read-only admin view. Only the class owner can edit reports.
      </p>
    </PageContainer>
  );
}

export default function ManageClassOwnership() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { classId } = useParams();

  if (classId) {
    return <ClassDetail schoolId={schoolId} classId={classId} />;
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My School
      </Link>
      <h1 className="font-display text-2xl font-normal text-foreground mb-6">All Classes</h1>
      <SchoolClassList schoolId={schoolId} />
    </PageContainer>
  );
}
